import { useEffect, useRef, useState } from 'react';
import { useCanvas } from '../../contexts/CanvasContext';
import { useDesignerStore } from '../../stores';
import { useHistoryStore } from '../../stores/historyStore';
import { useCanvasOperations } from '../../hooks/useCanvasOperations';
import { t } from '../../i18n';
import styles from './FabricCanvas.module.css';

// Import fabric for side effects
import 'fabric';

interface FabricCanvasProps {
  onReady?: () => void;
}

export const FabricCanvas = ({ onReady }: FabricCanvasProps) => {
  const canvasElementRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { canvas, initCanvas, isReady } = useCanvas();
  const { zoom, setSelection, clearSelection, template, isLoading } = useDesignerStore();
  const { saveSnapshot } = useHistoryStore();
  const { addText, addImageFromFile } = useCanvasOperations();
  const [hasUserContent, setHasUserContent] = useState(false);
  const hasTemplate = Boolean(template?.baseImage);

  useEffect(() => {
    if (canvasElementRef.current && !isReady) {
      initCanvas(canvasElementRef.current);
    }
  }, [initCanvas, isReady]);

  useEffect(() => {
    if (isReady && onReady) {
      onReady();
    }
  }, [isReady, onReady]);

  useEffect(() => {
    if (!canvas) return;
    const center = new window.fabric.Point(canvas.width! / 2, canvas.height! / 2);
    canvas.zoomToPoint(center, zoom);
    canvas.renderAll();
  }, [canvas, zoom]);

  useEffect(() => {
    if (!canvas) return;

    const refreshUserContent = () => {
      const count = canvas
        .getObjects()
        .filter(
          (obj) =>
            !obj.isBaseImage &&
            !obj.isMaskOverlay &&
            !obj.isGuide &&
            !obj.isMockup
        ).length;
      setHasUserContent(count > 0);
    };

    const handleSelectionCreated = () => {
      const activeObject = canvas.getActiveObject();
      if (activeObject) {
        const ids = canvas.getActiveObjects().map((obj) => obj.id || '').filter(Boolean);
        setSelection(ids, activeObject);
      }
    };

    const handleSelectionUpdated = () => {
      const activeObject = canvas.getActiveObject();
      if (activeObject) {
        const ids = canvas.getActiveObjects().map((obj) => obj.id || '').filter(Boolean);
        setSelection(ids, activeObject);
      }
    };

    const handleSelectionCleared = () => {
      clearSelection();
    };

    const handleObjectModified = () => {
      const state = JSON.stringify(canvas.toJSON(['id', 'isGuide', 'isMockup', 'isBaseImage', 'isMaskOverlay']));
      saveSnapshot(state);
    };

    canvas.on('selection:created', handleSelectionCreated);
    canvas.on('selection:updated', handleSelectionUpdated);
    canvas.on('selection:cleared', handleSelectionCleared);
    canvas.on('object:modified', handleObjectModified);
    canvas.on('object:added', refreshUserContent);
    canvas.on('object:removed', refreshUserContent);

    refreshUserContent();
    const initialState = JSON.stringify(canvas.toJSON(['id', 'isGuide', 'isMockup', 'isBaseImage', 'isMaskOverlay']));
    saveSnapshot(initialState);

    return () => {
      canvas.off('selection:created', handleSelectionCreated);
      canvas.off('selection:updated', handleSelectionUpdated);
      canvas.off('selection:cleared', handleSelectionCleared);
      canvas.off('object:modified', handleObjectModified);
      canvas.off('object:added', refreshUserContent);
      canvas.off('object:removed', refreshUserContent);
    };
  }, [canvas, setSelection, clearSelection, saveSnapshot]);

  const handleEmptyStateUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await addImageFromFile(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const showEmptyState = !isLoading && !hasUserContent;

  return (
    <div className={styles.canvasWrapper}>
      <canvas ref={canvasElementRef} id="designCanvas" />
      {showEmptyState && (
        <div
          className={`${styles.emptyState} ${hasTemplate ? styles.emptyStateOverlay : styles.emptyStateNoTemplate}`}
          role="status"
        >
          <div className={styles.emptyStateInner}>
            <svg
              className={styles.emptyIcon}
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <h3 className={styles.emptyTitle}>
              {hasTemplate ? t('design.empty_add', 'Add your design') : t('design.empty_upload', 'Upload your logo or design')}
            </h3>
            <p className={styles.emptyDescription}>
              {hasTemplate
                ? t('design.empty_hint_template', 'Drop in a logo, type custom text, or pick a shape from the toolbar.')
                : t('design.empty_hint_upload', 'Drag & drop an image, or click below to browse. JPG, PNG, SVG, PDF, AI, EPS, PSD.')}
            </p>
            <div className={styles.emptyActions}>
              <button
                type="button"
                className={styles.emptyPrimary}
                onClick={handleEmptyStateUpload}
              >
                {t('design.upload_image', 'Upload image')}
              </button>
              <button
                type="button"
                className={styles.emptySecondary}
                onClick={() => addText()}
              >
                {t('design.add_text', 'Add text')}
              </button>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFilePicked}
            style={{ display: 'none' }}
          />
        </div>
      )}
    </div>
  );
};

export default FabricCanvas;
